import { useEffect, useMemo, useState } from 'react';
import { Button, Card, cn } from '@playerbase/ui';

import type { BuildChange } from '../games/build-timeline.js';
import { BuildChangedRow } from './build-changed-row.js';
import { interleaveBuildMarkers } from './interleave-build-markers.js';
import { PollGroupCard } from './poll-group-card.js';
import type { InboxEntityFilter, InboxGroup, InboxMessage } from './use-feedback-inbox.js';

// ---------------------------------------------------------------------------
// Polls feed.
//
// Companion to the chronological message feed, sitting in the right-
// hand sidebar slot. Two tabs (Active / Closed) split the same poll
// group set; an independent channel-chip filter scopes either tab.
// Visible cap of 10 with a Show-more button matches the message feed
// and activity panel's rhythm.
//
// The active/closed split is computed here rather than in the hook
// so a poll that happens to expire while the page is open re-routes
// from Active to Closed on the next render without a refetch.
// ---------------------------------------------------------------------------

const INITIAL_VISIBLE = 10;
const VISIBLE_STEP = 10;

interface PollsFeedProps {
  groups: InboxGroup[];
  byExternalId: Map<string, InboxMessage>;
  // Shared channel filter from the parent - the polls feed no
  // longer carries its own chip strip; the page-level filter
  // applies to all three feeds (activity, messages, polls).
  entityFilter: InboxEntityFilter | null;
  // Build-change divider rows, interleaved into the poll list by time.
  // Game-wide; the same set the message + form feeds receive.
  buildChanges?: BuildChange[];
  // Active/Closed tab. Lifted to the parent so the activity-routing
  // flow can flip the tab when a closed-poll activity is clicked
  // while Active is showing (or vice versa).
  activeTab: 'active' | 'closed';
  onTabChange: (tab: 'active' | 'closed') => void;
  // Highlight integration mirrors FeedbackInbox's: when an activity
  // entry is clicked and the matched row lives in this feed, the
  // parent sets these props so PollGroupCard can pulse the matching
  // row and the parent's effect can scrollIntoView via the registrar.
  highlightRowId?: string | null;
  forceOpenGroupId?: string | null;
  registerRowRef?: (rowId: string, el: HTMLDivElement | null) => void;
  // Override for the Card's height. Default keeps the original
  // viewport-bounded sidebar treatment; the new column layout
  // passes a fixed value (e.g. h-[28rem]) so polls + messages have
  // a stable visual rhythm regardless of row count.
  heightClass?: string;
}

// Replicates the feedback-inbox `entityFilterFor` predicate but
// applied client-side over an already-loaded group. Polls aren't
// channel-filtered at SQL time (the polls feed has its own chip
// state independent of the message feed), so we re-implement the
// match here against the head row.
//
// Exported so the inbox tab strip can compute a count using exactly
// the same predicate the feed renders with.
export function groupMatchesEntityFilter(
  g: InboxGroup,
  filter: InboxEntityFilter | null,
): boolean {
  if (filter === null) return true;
  const head = g.head;
  const headMessage = head.messages[0];
  if (filter.kind === 'forum') {
    // Forum messages live inside threads whose parent name equals
    // the forum's name (denormalised on the row in migration 0017).
    return head.discordParentChannelName === filter.name;
  }
  if (filter.kind === 'channel') {
    // Match the channel directly, or messages in a thread under it.
    if (head.discordChannelId === filter.channelId) return true;
    return headMessage?.discordParentChannelId === filter.channelId;
  }
  // thread
  return head.discordChannelId === filter.channelId;
}

// Exported so FeedbackInbox's ?focus deep-link resolver can decide
// which sub-tab (Active vs Closed) to switch to when the focused id
// matches a poll group. Same predicate the feed uses to split groups,
// so the deep-link lands the row on the same side the eye expects.
export function isClosed(g: InboxGroup, now: number): boolean {
  const status = g.head.pollStatus;
  if (status === null) return false;
  if (status.finalised) return true;
  if (status.expiresAt === null) return false;
  return new Date(status.expiresAt).getTime() <= now;
}

interface TabButtonProps {
  label: string;
  active: boolean;
  onClick: () => void;
  count: number;
}

function TabButton({ label, active, onClick, count }: TabButtonProps): JSX.Element {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      className={cn(
        'rounded px-2.5 py-1 text-xs font-medium font-mono transition-colors',
        'hover:bg-surface-3',
        active ? 'bg-surface-3 text-text shadow-sm' : 'text-text-muted',
      )}
    >
      {label}
      <span className="ml-1.5 tabular-nums text-text-muted">{count}</span>
    </button>
  );
}

export function PollsFeed({
  groups,
  byExternalId,
  entityFilter,
  buildChanges = [],
  activeTab,
  onTabChange,
  highlightRowId,
  forceOpenGroupId,
  registerRowRef,
  heightClass = 'max-h-[calc(100vh-7rem)]',
}: PollsFeedProps): JSX.Element {
  const [visible, setVisible] = useState<number>(INITIAL_VISIBLE);
  // Reset the cap when the user changes tab or filter - a fresh
  // view starts at the top, same rhythm as the message feed.
  const filterKey =
    entityFilter === null
      ? 'all'
      : entityFilter.kind === 'forum'
        ? `forum:${entityFilter.name}`
        : `${entityFilter.kind}:${entityFilter.channelId}`;
  useEffect(() => {
    setVisible(INITIAL_VISIBLE);
  }, [activeTab, filterKey]);

  const filter = entityFilter;

  // Apply the channel filter BEFORE splitting into active/closed so
  // the tab counts ("Active 4", "Closed 4") reflect what the user
  // would actually see if they switched tabs. Otherwise picking a
  // channel with no polls would still show the global totals next to
  // the empty state, which reads as a bug.
  const { active, closed } = useMemo(() => {
    const now = Date.now();
    const a: InboxGroup[] = [];
    const c: InboxGroup[] = [];
    for (const g of groups) {
      if (g.head.pollStatus === null) continue;
      if (!groupMatchesEntityFilter(g, filter)) continue;
      (isClosed(g, now) ? c : a).push(g);
    }
    return { active: a, closed: c };
  }, [groups, filter]);

  const filtered = activeTab === 'active' ? active : closed;
  const visibleGroups = filtered.slice(0, visible);
  const hasMore = visible < filtered.length;

  return (
    <Card className={cn('flex flex-col bg-surface', heightClass)}>
      <Card.Header className="rounded-t-md border-b border-border bg-surface pb-4">
        <div className="flex items-center justify-between gap-2">
          <h2 className="text-sm font-medium text-text">Polls</h2>
          <div
            role="tablist"
            aria-label="Active or closed polls"
            className="flex gap-1 rounded-md border border-border bg-surface p-0.5"
          >
            <TabButton
              label="Active"
              active={activeTab === 'active'}
              onClick={() => onTabChange('active')}
              count={active.length}
            />
            <TabButton
              label="Closed"
              active={activeTab === 'closed'}
              onClick={() => onTabChange('closed')}
              count={closed.length}
            />
          </div>
        </div>
      </Card.Header>
      <Card.Content className="no-scrollbar flex-1 overflow-y-auto p-0">
        <div
          key={`${activeTab}|${filterKey}`}
          className="motion-safe:animate-in motion-safe:fade-in-0 motion-safe:duration-150"
        >
          {filtered.length === 0 ? (
            <p className="px-4 py-6 text-text-muted text-sm">
              {activeTab === 'active'
                ? 'No active polls in this scope.'
                : 'No closed polls in this scope.'}
            </p>
          ) : (
            <ul className="divide-y divide-border">
              {interleaveBuildMarkers(
                visibleGroups,
                buildChanges,
                (g) => g.chronologicalAt,
              ).map((row) =>
                row.kind === 'build' ? (
                  <li key={`build:${row.change.marker.id}`}>
                    <BuildChangedRow marker={row.change.marker} />
                  </li>
                ) : (
                  <li key={row.item.id}>
                    <PollGroupCard
                      group={row.item}
                      byExternalId={byExternalId}
                      highlighted={
                        forceOpenGroupId === row.item.id ||
                        highlightRowId === row.item.head.id
                      }
                      innerRef={
                        registerRowRef === undefined
                          ? undefined
                          : (el) => registerRowRef(row.item.head.id, el)
                      }
                    />
                  </li>
                ),
              )}
            </ul>
          )}
          {hasMore && (
            <div className="flex justify-center border-t border-border px-4 py-3">
              <Button
                variant="ghost"
                size="sm"
                onClick={() =>
                  setVisible((c) => Math.min(filtered.length, c + VISIBLE_STEP))
                }
              >
                Show more
              </Button>
            </div>
          )}
        </div>
      </Card.Content>
    </Card>
  );
}
